import {
  DEFAULT_THEME_COLOR,
  WEB_ENTRY_LIMITS as L,
  type WebEntryDto,
  type WebEntryUpsert,
} from "../../api/webEntryService";

/** 接入点表单的草稿形状 + 校验 / 转换(纯函数,单测直接测)。 */
export type Draft = {
  name: string;
  agentName: string;
  welcomeText: string;
  quickQuestions: string[];
  themeColor: string;
  leadEnabled: boolean;
  enabled: boolean;
  /** 头像原值(objectKey / 绝对 URL);空串 = 内置机器人图。 */
  avatarUrl: string;
};

export function toDraft(e: WebEntryDto | null): Draft {
  return {
    name: e?.name ?? "",
    agentName: e?.agentName ?? "",
    welcomeText: e?.welcomeText ?? "",
    quickQuestions: e?.quickQuestions?.length ? [...e.quickQuestions] : [],
    themeColor: e?.themeColor || DEFAULT_THEME_COLOR,
    leadEnabled: e?.leadEnabled ?? true,
    enabled: e?.enabled ?? true,
    avatarUrl: e?.avatarUrl ?? "",
  };
}

/** 校验:返回 i18n key(platform:channelMgmt.webEntry.form.*);null = 通过。导出给单测。 */
export function validateWebEntryDraft(d: Draft): string | null {
  const name = d.name.trim();
  if (!name) return "form.errNameRequired";
  if (name.length > L.name) return "form.errNameTooLong";
  if (d.agentName.trim().length > L.agentName) return "form.errAgentNameTooLong";
  if (d.welcomeText.trim().length > L.welcomeText) return "form.errWelcomeTooLong";
  const qs = d.quickQuestions.map((q) => q.trim()).filter(Boolean);
  if (qs.length > L.quickQuestions) return "form.errQuickTooMany";
  if (qs.some((q) => q.length > L.quickQuestion)) return "form.errQuickTooLong";
  if (!/^#[0-9a-fA-F]{6}$/.test(d.themeColor)) return "form.errColor";
  return null;
}

export function draftToUpsert(d: Draft): WebEntryUpsert {
  return {
    name: d.name.trim(),
    agentName: d.agentName.trim(),
    welcomeText: d.welcomeText.trim(),
    quickQuestions: d.quickQuestions.map((q) => q.trim()).filter(Boolean),
    themeColor: d.themeColor.toUpperCase(),
    leadEnabled: d.leadEnabled,
    enabled: d.enabled,
    // 空串 = 清除头像(回内置机器人图);后端按「空 = 默认」处理
    avatarUrl: d.avatarUrl.trim(),
  };
}

/** 能直接当 <img src> 用的地址(http(s) / 站内绝对路径 / blob / data);私有桶 objectKey 不行。 */
export function isDisplayableUrl(v: string | null | undefined): boolean {
  return !!v && /^(https?:\/\/|\/|blob:|data:)/.test(v);
}

/** 头像预览地址:刚上传的临时地址 > api 给的预览地址 > 原值本身是可显示地址;都没有返回 null。 */
export function avatarPreviewSrc(
  avatarUrl: string,
  uploadedPreview: string | null,
  dtoPreview: string | null | undefined
): string | null {
  if (!avatarUrl) return null;
  if (uploadedPreview) return uploadedPreview;
  if (dtoPreview) return dtoPreview;
  return isDisplayableUrl(avatarUrl) ? avatarUrl : null;
}

export const AVATAR_MAX_BYTES = 2 * 1024 * 1024;
export const AVATAR_ACCEPT = "image/png,image/jpeg,image/webp,image/gif";

/** 头像文件校验:返回 i18n key(platform:channelMgmt.webEntry.form.*);null = 通过。 */
export function validateAvatarFile(f: { type: string; size: number }): string | null {
  if (!f.type.startsWith("image/")) return "form.errAvatarType";
  if (f.size > AVATAR_MAX_BYTES) return "form.errAvatarSize";
  return null;
}
